/**
 * Makro-Leiste im Ticket (Spec 8.2): ein Klick, alle Aktionen.
 *
 * Was der Server macht, macht der Server. Diese Komponente hat genau eine
 * eigene Aufgabe: die `insert_snippet`-Aktionen aus der Antwort in den
 * Editor legen — das ist der Teil, den ein Server nicht erledigen kann, ohne
 * eine Nachricht zu schreiben (und das darf er nicht, Spec 8.2).
 */
import type { Macro, MacroRunResult } from '@tickethub/shared';
import { Wand2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';

type MacroBarProps = {
  macros: Macro[];
  disabled: boolean;
  onRun: (macro: Macro) => Promise<MacroRunResult | null>;
};

export function MacroBar({ macros, disabled, onRun }: MacroBarProps) {
  const { t } = useTranslation();
  const [busyId, setBusyId] = useState<string | null>(null);

  if (macros.length === 0) return null;

  async function run(macro: Macro) {
    setBusyId(macro.id);
    try {
      await onRun(macro);
    } finally {
      setBusyId(null);
    }
  }

  return (
    <section className="flex flex-wrap items-center gap-2" aria-label={t('macro.title')}>
      <span className="flex items-center gap-1 text-xs font-medium text-muted-foreground">
        <Wand2 aria-hidden="true" className="size-3" />
        {t('macro.title')}
      </span>
      {macros.map((macro) => (
        <Button
          key={macro.id}
          type="button"
          size="sm"
          variant="outline"
          title={macro.description || undefined}
          disabled={disabled || busyId !== null}
          onClick={() => void run(macro)}
        >
          {busyId === macro.id ? t('common.saving') : macro.title}
        </Button>
      ))}
    </section>
  );
}
